/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import bcrypt from "bcryptjs";
import cn from "classnames";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import openToast from "utils/openToast";

import { resetPasswordApi } from "lib/v3/loginAPI";
import { useAuth } from "state";

import HidePasswordIcon from "components/icons/HidePasswordIcon";
import ShowPasswordIcon from "components/icons/ShowPasswordIcon";

import { ResetPasswordRequest } from "pages/Login/components/Forms/ResetPassword/ResetPassword";

import styles from "../../style.module.css";

import PasswordValidator, { checkPassword } from "./PasswordValidator";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import Label from "components/ui/Label";
import { Input } from "components/ui/Input";
import { Button } from "components/ui/Button";

interface IFormInput {
  repeatedPassword: string;
  password: string;
}

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRepeatedPasswordShown, setIsRepeatedPasswordShown] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm<IFormInput>({
    defaultValues: {
      password: "",
      repeatedPassword: "",
    },
  });

  const { handleLogin } = useAuth();
  const password = watch("password");
  const repeatedPassword = watch("repeatedPassword");

  const { mutate: resetPassword } = useMutation({
    mutationFn: (data: ResetPasswordRequest) => resetPasswordApi(data),
    onSuccess: (res) => {
      const { token, reactapp_v, setting_id } = res.data;
      if (token) {
        localStorage.setItem("role", "admin");
        localStorage.setItem("token", token);
      }

      if (reactapp_v) {
        localStorage.setItem("version", reactapp_v);
      }

      if (setting_id) {
        localStorage.setItem("setting_id", setting_id);
      }

      navigate("/");
      handleLogin(res.data, "admin");
    },
    onError: (error: any) => {
      setIsLoading(false);
      openToast(error.response.data.error, "error");
    },
  });

  const PASSWORD_SALT = useEnvs((state) => state.password_salt);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    setIsLoading(true);

    const hashPassword = bcrypt.hashSync(data.password, PASSWORD_SALT);

    const hashRepeatedPassword = bcrypt.hashSync(
      data.repeatedPassword,
      PASSWORD_SALT,
    );

    if (hashPassword !== hashRepeatedPassword) {
      openToast("Password confirmation doesn't match the Password.", "error");
      setIsLoading(false);
      return;
    }

    resetPassword({
      password: hashPassword,
      password_confirmation: hashRepeatedPassword,
    });
  };

  const isPasswordValid = Object.values(checkPassword(password)).every(
    (value) => value === true,
  );

  const isDisabled =
    isLoading ||
    !isPasswordValid ||
    (password === "" && repeatedPassword === "") ||
    password !== repeatedPassword;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack className="space-y-10">
        <Stack className="space-y-8">
          <Stack>
            <h3 className="text-white text-xl font-bold">
              Reset Your Password
            </h3>
            <Text className="text-base" style={{ color: "#9A9A9A" }}>
              You are one step away from regaining access to your account. Enter
              a new password below to complete the password reset process.{" "}
            </Text>
          </Stack>

          <Stack className="relative">
            <Label
              className="text-lg font-bold text-gray-200"
              isRequired
              variant={"dark"}
              isBold={false}
              htmlFor="password"
            >
              New Password
            </Label>

            <PasswordValidator
              password={password}
              register={register("password")}
              hasSpecialChar
            />
          </Stack>

          <Stack className="relative">
            <Label
              htmlFor="username"
              className="text-lg font-bold text-gray-200"
              isRequired
              variant={"dark"}
              isBold={false}
            >
              Repeat Password
            </Label>

            <Input
              className="border-[#535353]"
              type={isRepeatedPasswordShown ? "text" : "password"}
              id="password"
              placeholder="*****"
              {...register("repeatedPassword")}
            />

            <button
              type="button"
              className="absolute flex right-3 items-center bg-secondary-accent h-fit top-11 rounded-full"
              onClick={() =>
                setIsRepeatedPasswordShown(!isRepeatedPasswordShown)
              }
            >
              {isRepeatedPasswordShown ? (
                <HidePasswordIcon />
              ) : (
                <ShowPasswordIcon />
              )}
            </button>

            {errors.repeatedPassword && (
              <Text>
                Invalid login information, please check and try again.
              </Text>
            )}
          </Stack>
        </Stack>

        <div>
          <Button
            variant="primary"
            type="submit"
            size="xl"
            shouldPreventDefault={false}
            isLoading={isLoading}
            className={cn({
              [styles.disabled]: isDisabled,
            })}
            disabled={isDisabled}
            id="loginBtn"
          >
            Set New Password
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export default ResetPassword;
